/**
 * Single shared QueryClient + central query-key registry.
 *
 * Centralizing keys lets us invalidate consistently from mutations and avoids
 * stringly-typed cache misses across modules.
 */
import { QueryClient } from "@tanstack/react-query";

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30 * 1000, // 30s — feels instant on revisit, still fresh
      gcTime: 5 * 60 * 1000, // 5 min in-memory cache
      retry: 1,
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: 0,
    },
  },
});

export const queryKeys = {
  auth: {
    me: ["auth", "me"],
  },
  profile: ["profile"],
  cashAccounts: ["cash-accounts"],
  dashboard: ["dashboard"],
  weeklyReport: ["report", "weekly"],
  receivables: ["receivables"],
  payments: ["payments"],
  movements: (limit) => ["movements", { limit }],
  history: (start, end) => ["history", { start, end }],
  fxPnl: (start, end) => ["fx-pnl", { start, end }],
  netWorthSeries: (days, currency) => ["net-worth-series", { days, currency }],
  forecast: (days) => ["forecast", { days }],
  reminders: {
    preview: ["reminders", "preview"],
    last: ["reminders", "last"],
  },
  investments: ["investments"],
  debts: ["debts"],
  goals: ["goals"],
  chat: {
    history: ["chat", "history"],
  },
  integrations: {
    telegram: ["integrations", "telegram"],
    whatsapp: ["integrations", "whatsapp"],
  },
};

/** Convenience: invalidate every cache that depends on user-scoped data. */
export async function invalidateAfterMutation() {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: queryKeys.dashboard }),
    queryClient.invalidateQueries({ queryKey: queryKeys.weeklyReport }),
    queryClient.invalidateQueries({ queryKey: queryKeys.receivables }),
    queryClient.invalidateQueries({ queryKey: ["receivables-infinite"] }),
    queryClient.invalidateQueries({ queryKey: queryKeys.payments }),
    queryClient.invalidateQueries({ queryKey: ["payments-infinite"] }),
    queryClient.invalidateQueries({ queryKey: queryKeys.movements(100) }),
    queryClient.invalidateQueries({ queryKey: ["movements-infinite"] }),
    queryClient.invalidateQueries({ queryKey: queryKeys.investments }),
    queryClient.invalidateQueries({ queryKey: queryKeys.debts }),
    queryClient.invalidateQueries({ queryKey: queryKeys.goals }),
    queryClient.invalidateQueries({ queryKey: queryKeys.profile }),
  ]);
}
